import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, ArrowRight, ArrowLeft, Volume2, Loader2, Check, RotateCcw, Languages } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const LANGS = [
  { id: "en", label: "English" },
  { id: "hi", label: "हिन्दी" },
  { id: "hinglish", label: "Hinglish" },
];

// Each step: where to go, what to point at, and one plain sentence per language.
const STEPS = [
  { to: "/command-center", target: "athlete-table",
    en: { t: "This is your team list", b: "Every athlete you coach sits here. The numbers say who is fresh and who is tired today. Tap Next." },
    hi: { t: "यह आपकी टीम की लिस्ट है", b: "आपके सभी खिलाड़ी यहाँ हैं। नंबर बताते हैं कौन आज फ्रेश है और कौन थका है। Next दबाइए।" },
    hinglish: { t: "Ye aapki team ki list hai", b: "Aapke sab athletes yahan hain. Number batate hain kaun aaj fresh hai aur kaun thaka hai. Next dabaiye." } },
  { to: "/athlete", target: "score-strip",
    en: { t: "Four numbers per athlete", b: "Readiness = ready to train. Sharpness = skill is firing. Effort = how much work. Rest = how well they recovered. Higher is better." },
    hi: { t: "हर खिलाड़ी के चार नंबर", b: "Readiness = ट्रेनिंग के लिए तैयार। Sharpness = स्किल कैसी चल रही है। Effort = कितना काम किया। Rest = कितनी रिकवरी हुई। ज़्यादा = अच्छा।" },
    hinglish: { t: "Har athlete ke chaar number", b: "Readiness = training ke liye taiyaar. Sharpness = skill kaisi chal rahi hai. Effort = kitna kaam. Rest = kitni recovery. Zyada = accha." } },
  { to: "/athlete?tab=plan", target: "add-task-btn",
    en: { t: "Add today's session", b: "Tap Add task to write what the athlete will do today — gym, skill, rest, anything. The app learns from it." },
    hi: { t: "आज का सेशन जोड़िए", b: "Add task दबाकर लिखिए आज खिलाड़ी क्या करेगा — जिम, स्किल, आराम, कुछ भी। ऐप उससे सीखता है।" },
    hinglish: { t: "Aaj ka session add kijiye", b: "Add task dabakar likhiye aaj athlete kya karega — gym, skill, rest, kuch bhi. App usse seekhta hai." } },
  { to: "/add-athlete", target: "save-athlete",
    en: { t: "Add your own athlete", b: "Name, sport, age, training and goal is enough. Add a photo if you like. That is the whole tour — ask the chat button anything, in your own language." },
    hi: { t: "अपना खिलाड़ी जोड़िए", b: "नाम, खेल, उम्र, ट्रेनिंग और गोल — बस इतना काफ़ी है। फ़ोटो चाहें तो लगाइए। टूर पूरा — नीचे चैट बटन पर अपनी भाषा में कुछ भी पूछिए।" },
    hinglish: { t: "Apna athlete add kijiye", b: "Naam, sport, umar, training aur goal — itna kaafi hai. Photo chahein to lagaiye. Tour poora — neeche chat button par apni bhasha me kuch bhi poochiye." } },
];

const TOUR_KEY = "xps.tour.lang";

export default function GuidedTour({ open, onClose, isDemo }) {
  const navigate = useNavigate();
  const [i, setI] = useState(0);
  const [lang, setLang] = useState(() => localStorage.getItem(TOUR_KEY) || "en");
  const [speaking, setSpeaking] = useState(false);
  const [resetting, setResetting] = useState(false);
  const audioRef = useRef(null);
  const step = STEPS[i];
  const copy = step?.[lang] || step?.en;

  useEffect(() => { localStorage.setItem(TOUR_KEY, lang); }, [lang]);

  // drive the app: go to the page, then highlight the thing we are talking about
  useEffect(() => {
    if (!open || !step) return;
    navigate(step.to);
    try { localStorage.setItem("xps.tour.step", `${copy.t} — ${copy.b}`); } catch { /* ignore */ }
    const id = setTimeout(() => {
      const el = document.querySelector(`[data-testid="${step.target}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("tour-spot");
      setTimeout(() => el.classList.remove("tour-spot"), 3200);
    }, 900);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, i, lang]);

  useEffect(() => () => { audioRef.current?.pause?.(); }, []);

  if (!open || !step) return null;

  const speak = async () => {
    setSpeaking(true);
    try {
      const { data } = await api.post("/tts", { text: `${copy.t}. ${copy.b}`, voice: "nova" });
      audioRef.current?.pause?.();
      const a = new Audio(`${API_BASE}${data.url}`);
      audioRef.current = a;
      a.onended = () => setSpeaking(false);
      a.onerror = () => setSpeaking(false);
      await a.play();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voice is unavailable right now");
      setSpeaking(false);
    }
  };

  const finish = async () => {
    audioRef.current?.pause?.();
    try { await api.post("/me/tutorial-complete"); } catch { /* ignore */ }
    onClose?.();
  };

  const resetSandbox = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/demo/reset");
      toast.success("Demo reset", { description: `${data.demo_athletes} sample athletes back to day one` });
      setI(0);
      navigate("/");
      window.dispatchEvent(new CustomEvent("trifit-synced", { detail: {} }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
    setResetting(false);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-[80] p-3 sm:p-4 pointer-events-none" data-testid="guided-tour">
      <div className="pointer-events-auto mx-auto w-full max-w-2xl bg-background border-2 border-foreground shadow-2xl">
        <div className="px-4 py-2.5 border-b border-border flex items-center gap-2 flex-wrap">
          <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground">
            Step {i + 1} of {STEPS.length}
          </span>
          <div className="flex-1 flex gap-1 min-w-[80px]">
            {STEPS.map((_, k) => (
              <span key={k} className={`h-1 flex-1 ${k <= i ? "bg-foreground" : "bg-border"}`} />
            ))}
          </div>
          <div className="inline-flex border border-border overflow-hidden" data-testid="tour-lang">
            <Languages className="w-3.5 h-3.5 self-center mx-1.5 text-muted-foreground" />
            {LANGS.map((l) => (
              <button key={l.id} data-testid={`tour-lang-${l.id}`} onClick={() => setLang(l.id)}
                className={`px-2 py-1 text-[11px] transition-colors ${lang === l.id ? "bg-foreground text-background" : "hover:bg-surface"}`}>
                {l.label}
              </button>
            ))}
          </div>
          <button data-testid="tour-close" onClick={finish} aria-label="Close tour"
            className="text-muted-foreground hover:text-foreground transition-colors"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 sm:p-5 flex gap-4 items-start">
          <button data-testid="tour-speak" onClick={speak} disabled={speaking} aria-label="Read this aloud"
            className="w-11 h-11 border border-foreground flex items-center justify-center shrink-0 hover:bg-surface transition-colors disabled:opacity-60">
            {speaking ? <Loader2 className="w-5 h-5 animate-spin" /> : <Volume2 className="w-5 h-5" />}
          </button>
          <div className="min-w-0">
            <h3 className="font-heading text-lg sm:text-xl font-black tracking-tight" data-testid="tour-title">{copy.t}</h3>
            <p className="text-sm sm:text-base mt-1 leading-snug" data-testid="tour-body">{copy.b}</p>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-border flex items-center gap-2 flex-wrap">
          <button data-testid="tour-back" onClick={() => setI((v) => Math.max(0, v - 1))} disabled={i === 0}
            className="text-sm px-3 py-2 border border-border hover:bg-surface transition-colors disabled:opacity-40 flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {isDemo && (
            <button data-testid="tour-reset" onClick={resetSandbox} disabled={resetting}
              title="Throw away everything you changed and start the demo again"
              className="text-sm px-3 py-2 border border-border hover:bg-surface transition-colors disabled:opacity-60 flex items-center gap-1.5">
              {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Reset demo
            </button>
          )}
          <button data-testid="tour-skip" onClick={finish}
            className="text-sm px-3 py-2 text-muted-foreground hover:text-foreground transition-colors ml-auto">Skip</button>
          {i < STEPS.length - 1 ? (
            <button data-testid="tour-next" onClick={() => setI((v) => Math.min(STEPS.length - 1, v + 1))}
              className="text-sm px-5 py-2 bg-foreground text-background hover:bg-neutral-800 transition-colors flex items-center gap-1.5">
              Next <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button data-testid="tour-finish" onClick={finish}
              className="text-sm px-5 py-2 bg-foreground text-background hover:bg-neutral-800 transition-colors flex items-center gap-1.5">
              <Check className="w-4 h-4" /> Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export const TOUR_STEP_TITLES = STEPS.map((s) => s.en.t);
